import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { IconSpinner, Page, PaginationLinks, DashboardLayout, StatusTabs } from '../../components';
import { withViewport } from '../../util/uiHelpers';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import OrderCards from './OrderCards/OrderCards';
import { INBOX_STATUS_TABS, fetchInboxCounts } from './InboxPage.duck';

import css from './InboxPage.module.css';

// Kept as a compatibility export for existing example/test imports.
export const InboxItem = () => null;

// Searches the orders list; the search covers all orders, so it clears the status tab
const OrderSearch = ({ tab, initialText }) => {
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const [text, setText] = useState(initialText || '');
  useEffect(() => setText(initialText || ''), [initialText]);

  const go = q =>
    history.push(
      createResourceLocatorString('InboxPage', routeConfiguration, { tab }, q ? { q } : {})
    );
  const onSubmit = e => {
    e.preventDefault();
    go(text.trim());
  };

  return (
    <form className={css.searchForm} onSubmit={onSubmit} role="search">
      <input
        className={css.searchInput}
        type="search"
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={intl.formatMessage({
          id: tab === 'sales' ? 'OrderList.searchSales' : 'OrderList.searchPurchases',
        })}
        aria-label={intl.formatMessage({ id: 'OrderList.searchLabel' })}
      />
      {initialText ? (
        <button type="button" className={css.clearSearch} onClick={() => go('')}>
          <FormattedMessage id="OrderList.clearSearch" />
        </button>
      ) : null}
    </form>
  );
};

export const InboxPageComponent = props => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    pagination,
    params,
    scrollingDisabled,
    transactions,
  } = props;

  const { tab } = params;
  const validTab = tab === 'orders' || tab === 'sales';
  const userId = currentUser?.id?.uuid;
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (!validTab || !userId) {
      return;
    }
    let cancelled = false;
    dispatch(fetchInboxCounts(tab)).then(result => !cancelled && setCounts(result));
    return () => {
      cancelled = true;
    };
  }, [dispatch, tab, validTab, userId]);

  if (!validTab) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const isSales = tab === 'sales';
  const search = parse(location.search);
  const status = INBOX_STATUS_TABS.includes(search.status) ? search.status : null;
  const searchText = search.q != null ? String(search.q).trim() : '';
  const title = intl.formatMessage({
    id: isSales ? 'OrderList.salesTitle' : 'OrderList.purchasesTitle',
  });

  const statusTabs = [null, ...INBOX_STATUS_TABS].map(key => ({
    key: key || 'all',
    name: 'InboxPage',
    params: { tab },
    search: key ? `?status=${key}` : undefined,
    label: intl.formatMessage({
      id: `OrderList.tab.${key || 'all'}`,
      // Buyers wait for the seller to ship ("Preparing") rather than shipping themselves
      ...(key === 'toShip' && !isSales ? { id: 'OrderList.tab.preparing' } : {}),
    }),
    count: counts[key || 'all'],
    selected: !searchText && status === key,
  }));

  const hasTransactions = !fetchInProgress && transactions.length > 0;
  const pagingLinks =
    hasTransactions && pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="InboxPage"
        pagePathParams={params}
        pageSearchParams={{
          ...(status ? { status } : {}),
          ...(searchText ? { q: searchText } : {}),
        }}
        pagination={pagination}
      />
    ) : null;

  const emptyMessageId = searchText
    ? 'OrderList.noSearchResults'
    : `OrderList.empty.${isSales ? 'sales' : 'purchases'}`;

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <DashboardLayout
        topbar={<TopbarContainer isDashboardHeader={true} currentPage="InboxPage" />}
      >
        <div className={css.orderList}>
          <h1 className={css.orderListTitle}>{title}</h1>
          <OrderSearch tab={tab} initialText={searchText} />
          {searchText ? (
            <p className={css.searchSummary}>
              <FormattedMessage
                id="OrderList.searchSummary"
                values={{ count: pagination?.totalItems || 0, text: searchText }}
              />
            </p>
          ) : (
            <StatusTabs
              tabs={statusTabs}
              ariaLabel={intl.formatMessage({ id: 'OrderList.tabsLabel' })}
            />
          )}

          {fetchOrdersOrSalesError ? (
            <p className={css.error}>
              <FormattedMessage id="InboxPage.fetchFailed" />
            </p>
          ) : null}

          {fetchInProgress ? (
            <div className={css.listLoading}>
              <IconSpinner />
            </div>
          ) : hasTransactions ? (
            <OrderCards transactions={transactions} currentUser={currentUser} />
          ) : !fetchOrdersOrSalesError ? (
            <p className={css.emptyState}>
              <FormattedMessage id={emptyMessageId} />
            </p>
          ) : null}

          {pagingLinks}
        </div>
      </DashboardLayout>
    </Page>
  );
};

const mapStateToProps = state => {
  const { fetchInProgress, fetchOrdersOrSalesError, pagination, transactionRefs } = state.InboxPage;
  const { currentUser } = state.user;

  return {
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    transactions: getMarketplaceEntities(state, transactionRefs),
  };
};

const InboxPage = compose(
  connect(mapStateToProps),
  withViewport
)(InboxPageComponent);

export default InboxPage;
